import React from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { FocusTrap } from "focus-trap-react";
import { motion } from "framer-motion";
import classNames from "classnames";
import { Menu } from "@default/general-components/navigation/menu";
import MenuTeaser from "./menu-teaser";
import BodyScrollLockWithGap from "@default/lib/body-scroll-lock-with-gap";
import useTranslate from "@default/hooks/use-translate";
import {
  mainMenuOpenAction,
  accessibilityMenuOpenAction,
} from "../../app-actions";
import ActionWrapper from "./action-wrapper";
import DashedLine from "@default/general-components/dashed-line";

const OverlayMenu = () => {
  const dispatch = useDispatch();
  const mainMenuOpen = useSelector(
    (reduxStore) => reduxStore.appStore.mainMenuOpen
  );
  const microSite = useSelector((reduxStore) => reduxStore.appStore.microSite);
  const isMobile = useSelector((state) => state.appStore.isMobile);
  const mainMenuName = microSite === "rbx-company" ? "RBX_MENU" : "MAIN";
  const _t = useTranslate();

  if (typeof window === "undefined") return null;

  const BottomPart = () => {
    return (
      <div className="bottom-part">
        <div className="wrapper">
          <DashedLine />
          <Menu
            menuName="HIGHLIGHT_MENU"
            showLabel={true}
            ariaLabel={_t("accessibility.highlight_menu")}
          />
        </div>
      </div>
    );
  };

  return createPortal(
    <FocusTrap
      active={mainMenuOpen}
      paused={!mainMenuOpen}
      focusTrapOptions={{
        onDeactivate: () => {
          dispatch(mainMenuOpenAction(false));
          dispatch(accessibilityMenuOpenAction(false));
        },
        escapeDeactivates: true,
        clickOutsideDeactivates: (e) =>
          !e.target.closest("#toggle-overlay-menu"),
        allowOutsideClick: (e) => !!e.target.closest("#toggle-overlay-menu"),
        initialFocus: "#close-overlay-menu",
        checkCanFocusTrap: (trapContainers) =>
          new Promise((resolve) => {
            const check = () => {
              const isVisible = trapContainers.some(
                (container) =>
                  window.getComputedStyle(container).display !== "none"
              );
              if (isVisible) {
                resolve();
              } else {
                requestAnimationFrame(check);
              }
            };
            requestAnimationFrame(check);
          }),
      }}
    >
      <motion.div
        id="overlay-menu"
        className={classNames("body-scroll-lock-ignore", {
          active: mainMenuOpen,
        })}
        tabIndex={mainMenuOpen ? undefined : -1}
        aria-hidden={!mainMenuOpen}
        inert={!mainMenuOpen ? "" : undefined}
        {...(!microSite && {
          initial: false,
          animate: {
            filter: mainMenuOpen ? "blur(0px)" : "blur(100px)",
            opacity: mainMenuOpen ? 1 : 0,
            scale: mainMenuOpen ? 1 : 0,
            display: mainMenuOpen ? "flex" : "none",
          },
          transition: {
            scale: {
              duration: mainMenuOpen ? 0.1 : 1000000,
              ease: "easeInOut",
            },
            opacity: { duration: 0.4, ease: "easeInOut" },
            filter: { duration: 0.4, ease: "easeInOut" },
          },
        })}
      >
        {mainMenuOpen && !microSite && <BodyScrollLockWithGap />}
        <div className="nav-wrap">
          {isMobile && <ActionWrapper />}
          <MenuTeaser />
          <div className="navigation-wrapper">
            <div className="upper-part">
              {!isMobile && <ActionWrapper />}
              <Menu
                navClassName="main-menu"
                menuName={mainMenuName}
                ariaLabel={_t("accessibility.main_menu")}
              />
            </div>
            {!microSite && !isMobile && <BottomPart />}
          </div>
          {!microSite && isMobile && <BottomPart />}
        </div>
      </motion.div>
    </FocusTrap>,
    document.body
  );
};

export default OverlayMenu;
